import { ExpandedRowVM } from 'src/components/dispatch/vehicleRowExpand/buildExpandedRowVM';
import { PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';
import { PositionEntity } from 'src/entities/PositionEntity';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';
import { rowIdFromTruck } from 'src/pages/ConvenienceDispatches/adapter';
import { coordKeyOf } from 'src/pages/ConvenienceDispatches/placeKeys';
import { VehicleRowVM } from 'src/pages/ConvenienceDispatches/vehicleRowVM';

import { buildGarageLegs } from './garageLegs';
import { resolveVehicleColor } from './vehicleColorPalette';

export type MapRouteMarkerVM = {
  seq: number;
  position: PositionEntity;
  kind: 'loading' | 'unloading';
  // 詳細パネルの選択解決 (resolveSelectionFromStop) に使う識別子。
  // rowId だけではどのカードかを特定できないため、停車地の由来をそのまま透過する。
  cardIndex: number;
  dispatchOrderIndex: number;
};

export type MapVehicleRouteVM = {
  rowId: string;
  truckId: number;
  rotationNumber: number;
  color: string;
  positions: PositionEntity[];
  markers: MapRouteMarkerVM[];
};

export type MapUnassignedMarkerVM = {
  /** 代表 id。React の key と、ツールチップの引き当てに使う */
  id: string;
  /** その地点にぶら下がる未割当案件すべて。先頭が代表 id */
  ids: string[];
  position: PositionEntity;
};

// card.kind に応じて legRuns から座標を解決する。同一 card 内の legRun は同一地点
// (placeKey が lat-lng ベース) なので先頭を代表として使う。解決不可は null。
// kind は 'loading'|'unloading'|'no_delivery'|'template' の4値。後2者は固定ルート由来の
// 合成カードで、積地/降地どちらの stop かは card.stopType が持つ (kind 自体には出ない)。
// stopType を見ずに「loading 以外は降地」と扱うと no_delivery/template の積地カードを
// 誤って降地座標で解決してしまうため、積地側かどうかを stopType も含めて判定する。
function resolveCardPosition(card: VehicleRowVM['cards'][number]): PositionEntity | null {
  const lr = card.legRuns[0];
  if (!lr) return null;
  const isLoadingSide =
    card.kind === 'loading' ||
    ((card.kind === 'no_delivery' || card.kind === 'template') && card.stopType === 'loading');
  if (isLoadingSide) {
    return lr.loading_lat != null && lr.loading_lng != null ? [lr.loading_lat, lr.loading_lng] : null;
  }
  return lr.unloading_lat != null && lr.unloading_lng != null ? [lr.unloading_lat, lr.unloading_lng] : null;
}

// orderIndexByTruckId: 左リスト表示順(orderedRowGroups)由来の Map。map と左リスト色バーで
// 共用し、車両 D&D 並替後も両者の palette fallback 色を一致させる (Codex Finding 2)。
// Map に無い truck (通常発生しない想定) のみ rows 内の index にフォールバックする。
//
// expandedRowVMByRowId: 展開パネルと同じ VM (buildExpandedRowVMByRowId が rowId 単位で作る)。
// マーカーの地点・番号(seriesIndex)をこの VM から作ることで、展開パネルと地図の番号を一致させる
// (/plans が place.seriesIndex をリストと地図の双方で使うのと同じ考え方)。
export function buildMapVehicleRoutes(
  rows: VehicleRowVM[],
  orderIndexByTruckId: Map<number, number>,
  expandedRowVMByRowId: ReadonlyMap<string, ExpandedRowVM>,
  /** 車両単位で解決済みの色。行ごとの row.truckColor だと同一車両で線の色が割れる */
  truckColorByTruckId: ReadonlyMap<number, string | null>,
  /** 車庫までの線を引くための delivery。渡さなければ車庫を足さない（従来の描画） */
  deliveryById?: ReadonlyMap<number, PlanningsDeliveryWithLegRunEntity>,
): MapVehicleRouteVM[] {
  // 車庫は最初の回転の前と最後の回転の後にだけ置く。回転ごとに挟むと、
  // 回転の変わり目で車庫を往復する線になる
  const rotationRange = new Map<number, { min: number; max: number }>();
  rows.forEach((row) => {
    const current = rotationRange.get(row.deliveryId);
    rotationRange.set(row.deliveryId, {
      min: Math.min(current?.min ?? row.rotationNumber, row.rotationNumber),
      max: Math.max(current?.max ?? row.rotationNumber, row.rotationNumber),
    });
  });

  return rows.map((row, index) => {
    const positions: PositionEntity[] = [];
    // polyline は従来どおり cards から作る。地点リストは isAssignedDelivery で
    // 絞るため、地点ベースにすると固定ルート由来の点が線から消える。
    for (const card of row.cards) {
      const pos = resolveCardPosition(card);
      if (pos) positions.push(pos);
    }

    // 車庫を線の両端に足す。地点がゼロの行に足すと車庫に点が立つだけの線になるので出さない
    if (deliveryById && positions.length > 0) {
      const range = rotationRange.get(row.deliveryId);
      const { start, end } = buildGarageLegs(
        deliveryById.get(row.deliveryId),
        row.rotationNumber === range?.min,
        row.rotationNumber === range?.max,
      );
      if (start) positions.unshift(start);
      if (end) positions.push(end);
    }

    // 番号マーカーは展開パネルと同じ地点・同じ番号にする（/plans と同じ考え方）。
    // 積地にもマーカーを出す（PlanningMapPositions.presenter.tsx:48 に絞り込みが無いのと同じ）。
    const rowId = rowIdFromTruck(row.truckId, row.rotationNumber, row.deliveryId);
    const markers: MapRouteMarkerVM[] = (expandedRowVMByRowId.get(rowId)?.stops ?? [])
      .filter((s) => s.position != null)
      .map((s) => ({
        seq: s.seriesIndex,
        position: s.position,
        kind: s.kind,
        cardIndex: s.cardIndex,
        dispatchOrderIndex: s.dispatchOrderIndex,
      }));

    return {
      rowId,
      truckId: row.truckId,
      rotationNumber: row.rotationNumber,
      color: resolveVehicleColor(
        truckColorByTruckId.get(row.truckId) ?? null,
        orderIndexByTruckId.get(row.truckId) ?? index,
      ),
      positions,
      markers,
    };
  });
}

/**
 * 未割当ピン。**地点ごとに 1 本**にまとめ、その地点の案件を `ids` にぶら下げる。
 *
 * Why まとめる: 案件ごとにピンを立てると同一地点でピンが重なり、
 * leaflet.markercluster がクラスターにする。クラスターの丸 (40px) はアンカーが
 * 地点の中心なので、同じ点に立つ配車済みの番号マーカー (22px・同じくアンカーは中心) を
 * 完全に覆い、下の配車済み案件を選べなくなる
 * (logposeTech/log_web_issues#2430)。旧地図 (V3Plans/Component.tsx:384-421) は
 * 座標ごとに ids をまとめた 1 ピンなので、この問題が起きていなかった。
 * 未割当ピン (red.png) 自体はアンカーが下端なので、1 本なら地点の上に描かれて
 * 中心は空いたままになる。
 *
 * Why 丸めたキーで束ねる: 同じ場所でも取り込み時期で座標の桁数が違う
 * (logposeTech/log_web_issues#314)。生の座標一致で束ねると同一地点が割れる。
 * 束ねるのはキーだけで、**ピンの座標は先頭 legRun の生の値**を使う (#314 と同じ方針)。
 */
export function buildMapUnassignedMarkers(
  unassignedLegRuns: ShipmentOrderLegRunForPlanEntity[],
): MapUnassignedMarkerVM[] {
  const byPlace = new Map<string, MapUnassignedMarkerVM>();
  // id フォールバックは「フィルタ後の出力位置」でなく「入力配列の index」を使う
  // (toUnassignedItem の fallbackIndex と同じ考え方)。座標欠損の除外で後続 index が
  // ずれても id が変わらないよう安定させる。
  for (let i = 0; i < unassignedLegRuns.length; i += 1) {
    const lr = unassignedLegRuns[i];
    if (lr.unloading_lat == null || lr.unloading_lng == null) continue;
    const id = lr.id != null ? `lr-${lr.id}` : `lr-idx-${i}`;
    // 座標が両方あることは上で確かめているので fallback は使われない
    const placeKey = coordKeyOf('unloading', lr, id);
    const existing = byPlace.get(placeKey);
    if (existing) existing.ids.push(id);
    else byPlace.set(placeKey, { id, ids: [id], position: [lr.unloading_lat, lr.unloading_lng] });
  }
  // Map は挿入順を保つので、地点の並びは初出順になる
  return [...byPlace.values()];
}
